import { useEffect, useEffectEvent } from 'react';

import type { Unsubscribe } from '@mint-lib/api-zod-support/socket';
import { useTranslation } from '@mint-lib/i18n';
import { enqueueSnackbar } from '@mint-lib/notifications';
import { useQueryClient } from '@mint-lib/query-client';
import { Suggestions } from '@mint-sdk/api-recommendations';
import {
  onAuthenticated,
  onError,
  onListUpdate,
  sendList,
  POLL_INTERVAL_IN_SECONDS,
} from '@mint-sdk/api-recommendations/socket';
import type { z } from 'zod';

import { useSuggestionFilter } from './useSuggestionFilter.js';

export const useWSListeners = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('@mint-lib/recommendations-circuit');
  const { selectedSuggestionFilter } = useSuggestionFilter();

  const handleAuthenticated = useEffectEvent(() => {
    sendList({
      ...selectedSuggestionFilter,
      pollInterval: POLL_INTERVAL_IN_SECONDS,
      pageNum: 1,
      pageSize: 10,
    });
  });

  const handleListUpdate = useEffectEvent(() => {
    const queryKey = Suggestions.listInfiniteQueryOptions({
      params: {
        ...selectedSuggestionFilter,
        page: 1,
        pageSize: 10,
      },
    }).queryKey;

    queryClient.refetchQueries({ queryKey, exact: true });
  });

  const handleError = useEffectEvent(
    ({ status, message }: z.infer<typeof onError.config.payloadSchema>) => {
      if (status === 'error') {
        enqueueSnackbar({
          variant: 'error',
          autoHideDuration: 4000,
          title: message,
          subTitle: t('Unavailable to refresh the suggestion'),
        });
      }
    },
  );

  useEffect(() => {
    const unsubscribeFns: Unsubscribe[] = [
      onAuthenticated(handleAuthenticated),
      onListUpdate(handleListUpdate),
      onError(handleError),
    ];

    return () => {
      for (const unsub of unsubscribeFns) unsub();
    };
  });
};
